import React, { useState, useEffect } from 'react';
import { PAGE, SettingsHeader } from './SettingsUI';
import {
  Waypoints, ShieldCheck, Copy, Check, Loader2, Users, Pencil, Plug,
  ExternalLink, Lock, Trash2, Info, Sparkles,
} from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import clsx from 'clsx';

/* ── Toggle ─────────────────────────────────────────────────────────────── */
function Toggle({ checked, onChange, disabled }) {
  return (
    <button type="button" onClick={() => !disabled && onChange(!checked)} disabled={disabled}
      className={clsx('relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus:outline-none',
        checked ? 'bg-primary-600' : 'bg-slate-300', disabled && 'opacity-50 cursor-not-allowed')}>
      <span className={clsx('inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform',
        checked ? 'translate-x-6' : 'translate-x-1')} />
    </button>
  );
}

function CopyField({ value }) {
  const [copied, setCopied] = useState(false);
  const copy = () => navigator.clipboard.writeText(value)
    .then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500); })
    .catch(() => toast.error('Copy failed'));
  return (
    <div className="flex items-stretch gap-2">
      <code className="flex-1 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 text-[13px] font-mono text-slate-700 overflow-x-auto whitespace-nowrap">{value}</code>
      <button onClick={copy} className={clsx('px-3 rounded-lg border text-sm font-medium inline-flex items-center gap-1.5 transition-colors',
        copied ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-slate-200 hover:bg-slate-50 text-slate-600')}>
        {copied ? <><Check size={15} /> Copied</> : <><Copy size={15} /> Copy</>}
      </button>
    </div>
  );
}

/* ── Numbered step ──────────────────────────────────────────────────────── */
function Step({ n, children }) {
  return (
    <li className="flex gap-3">
      <span className="flex-none w-6 h-6 rounded-full bg-primary-100 text-primary-700 text-xs font-bold grid place-items-center mt-0.5">{n}</span>
      <span className="text-sm text-slate-600 leading-relaxed">{children}</span>
    </li>
  );
}

function McpAssistantSteps() {
  return (
    <div className="space-y-5">
      <div>
        <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Any assistant with custom connectors</div>
        <ol className="space-y-2.5">
          <Step n={1}>In your assistant, open <strong>Settings → Connectors → Add custom connector</strong>.</Step>
          <Step n={2}>Name it <strong>Rostery</strong> and paste the server URL above.</Step>
          <Step n={3}>Click <strong>Connect</strong> — you're taken to the Rostery sign-in page.</Step>
          <Step n={4}>Sign in with your <strong>Rostery email &amp; password</strong>, then <strong>Allow access</strong>.</Step>
          <Step n={5}>Ask your assistant about your roster, clients, notes and incidents.</Step>
        </ol>
      </div>
    </div>
  );
}
function ChatGptSteps({ url }) {
  return (
    <div>
      <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">ChatGPT · Pro / Business / Enterprise</div>
      <ol className="space-y-2.5">
        <Step n={1}>Open <strong>Settings → Connectors → Add → Custom (MCP server)</strong>.</Step>
        <Step n={2}>Name it <strong>Rostery</strong> and paste the server URL above.</Step>
        <Step n={3}>Choose <strong>OAuth</strong> and click <strong>Connect / Authenticate</strong>.</Step>
        <Step n={4}>Sign in with your <strong>Rostery email &amp; password</strong>, then <strong>Allow access</strong>.</Step>
        <Step n={5}>Enable the Rostery connector in a chat and start asking.</Step>
      </ol>
      <p className="text-xs text-slate-400 mt-3">Requires a ChatGPT plan that supports custom connectors / developer mode.</p>
    </div>
  );
}

/* ── Provider card ──────────────────────────────────────────────────────── */
function ProviderCard({ active, name, sub, onClick }) {
  return (
    <button onClick={onClick}
      className={clsx('flex-1 text-left rounded-xl border p-4 transition-all bg-white',
        active ? 'border-primary-500 ring-2 ring-primary-100 shadow-sm' : 'border-slate-200 hover:border-slate-300')}>
      <div className="flex items-center justify-between">
        <div>
          <div className="font-semibold text-slate-800">{name}</div>
          <div className="text-xs text-slate-400 mt-0.5">{sub}</div>
        </div>
        <span className={clsx('w-5 h-5 rounded-full grid place-items-center', active ? 'bg-primary-600' : 'border-2 border-slate-200')}>
          {active && <Check size={12} className="text-white" strokeWidth={3} />}
        </span>
      </div>
    </button>
  );
}

/* ── Card shell ─────────────────────────────────────────────────────────── */
function Card({ title, icon: Icon, children, className }) {
  return (
    <section className={clsx('bg-white rounded-2xl border border-slate-200 shadow-sm', className)}>
      {title && (
        <div className="flex items-center gap-2 px-5 py-4 border-b border-slate-100">
          {Icon && <Icon size={16} className="text-primary-600" />}
          <h3 className="text-sm font-bold text-slate-900">{title}</h3>
        </div>
      )}
      <div className="p-5">{children}</div>
    </section>
  );
}

/* ── Page ───────────────────────────────────────────────────────────────── */
export default function AiAutomation() {
  const [s, setS] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [gated, setGated] = useState(false);

  const load = async () => {
    try { const { data } = await api.get('/mcp-admin/settings'); setS(data); setGated(false); }
    catch (e) {
      // A 403 here is not a failure, it is an answer: the organisation's plan does not include
      // API access. Reporting it as "Failed to load" sends the reader looking for a broken server
      // when the system is working exactly as configured.
      if (e.response?.status === 403) {
        setGated(true);
        toast.error(e.response?.data?.message || 'API access is not enabled on this organisation’s plan.');
      } else {
        toast.error(e.response?.data?.message || 'Could not load the AI & automation settings. Please try again.');
      }
    }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const patch = async (partial) => {
    const prev = s, next = { ...s, ...partial };
    setS(next); setSaving(true);
    try {
      await api.put('/mcp-admin/settings', {
        enabled: next.enabled, allow_backoffice: next.allow_backoffice,
        allow_write: next.allow_write, provider: next.provider,
      });
    } catch { toast.error('Failed to save'); setS(prev); }
    finally { setSaving(false); }
  };

  const revoke = async (name) => {
    try { await api.delete(`/mcp-admin/connections?client_name=${encodeURIComponent(name)}`); toast.success('Connection revoked'); load(); }
    catch { toast.error('Failed to revoke'); }
  };

  if (loading) return <div className="p-16 flex justify-center"><Loader2 className="animate-spin text-primary-600" size={26} /></div>;
  // Rendering nothing here left a blank page behind a toast that said "Failed to load", so the
  // honest answer — your plan does not include this — never reached the person reading it.
  if (gated) return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-16 text-center">
      <h1 className="text-lg font-semibold text-slate-800">AI &amp; Automation</h1>
      <p className="mt-3 text-sm text-slate-600">
        Connecting an AI assistant to this organisation needs <strong>API access</strong>, which is not
        part of this organisation&rsquo;s current plan.
      </p>
      <p className="mt-2 text-sm text-slate-500">
        Nothing is broken and nothing is lost &mdash; ask your account owner to add API access, and this
        page will open with your settings as they were.
      </p>
    </div>
  );
  if (!s) return null;
  const provider = s.provider || 'claude';

  return (
    <div className={PAGE}>
      <SettingsHeader
        title="AI & automation"
        description="Connect your AI assistant to Rostery, with full control over what it can access."
        actions={
          <span className={clsx('inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full',
            s.enabled ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-slate-100 text-slate-500 border border-slate-200')}>
            <span className={clsx('w-1.5 h-1.5 rounded-full', s.enabled ? 'bg-emerald-500' : 'bg-slate-400')} />
            {s.enabled ? 'Active' : 'Disabled'}
          </span>
        }
      />

      {/* Master enable card */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="p-5 flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <Waypoints size={22} className="text-primary-600 mt-0.5 flex-none" />
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="font-bold text-slate-900">MCP — External AI Model Access</h2>
                <span className="text-[10px] font-bold bg-slate-800 text-white px-1.5 py-0.5 rounded">BETA</span>
              </div>
              <p className="text-sm text-slate-500 mt-1 max-w-2xl">
                Model Context Protocol lets an AI assistant securely query and act on your Rostery data —
                review the roster, write notes, manage incidents and book shifts — always within your existing permissions.
              </p>
            </div>
          </div>
          <div className="flex flex-col items-end gap-2 flex-none">
            <Toggle checked={s.enabled} onChange={(v) => patch({ enabled: v })} />
            <span className="inline-flex items-center gap-1 text-[11px] text-slate-400"><Lock size={11} /> Admin only</span>
          </div>
        </div>
      </div>

      {s.enabled && (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
          {/* LEFT — Connect */}
          <div className="lg:col-span-7 space-y-6">
            <Card title="Choose an assistant" icon={Plug}>
              <div className="flex flex-col sm:flex-row gap-3 mb-5">
                <ProviderCard active={provider === 'claude'}  name="Other assistants" sub="Any MCP custom connector" onClick={() => patch({ provider: 'claude' })} />
                <ProviderCard active={provider === 'chatgpt'} name="ChatGPT" sub="OpenAI connectors"          onClick={() => patch({ provider: 'chatgpt' })} />
              </div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">MCP Server URL</label>
              <CopyField value={s.server_url} />
            </Card>

            <Card title="Connect in a few clicks" icon={ExternalLink}>
              {provider === 'claude' ? <McpAssistantSteps /> : <ChatGptSteps url={s.server_url} />}
            </Card>
          </div>

          {/* RIGHT — Access control */}
          <div className="lg:col-span-5 space-y-6">
            <Card title="Access control" icon={ShieldCheck}>
              <div className="space-y-3">
                <div className="flex items-start justify-between gap-3 rounded-xl border border-slate-200 p-3.5">
                  <div className="flex items-start gap-2.5">
                    <Users size={17} className="text-slate-500 mt-0.5" />
                    <div>
                      <div className="font-semibold text-slate-800 text-sm">Back office access</div>
                      <p className="text-xs text-slate-500 mt-0.5">Let coordinator/HR/ops/support connect (read-only). Off = admins only.</p>
                    </div>
                  </div>
                  <Toggle checked={s.allow_backoffice} onChange={(v) => patch({ allow_backoffice: v })} />
                </div>
                <div className="flex items-start justify-between gap-3 rounded-xl border border-amber-200 bg-amber-50/40 p-3.5">
                  <div className="flex items-start gap-2.5">
                    <Pencil size={17} className="text-amber-500 mt-0.5" />
                    <div>
                      <div className="font-semibold text-slate-800 text-sm">Write actions</div>
                      <p className="text-xs text-slate-500 mt-0.5">Manage shifts like the scheduler: create (including recurring), edit, assign, cancel, restore, delete, copy a week forward and publish. The scheduler's checks still apply (double-booking, funding, invoiced shifts). Notes, incidents, clients and money can't be changed. <strong>Admins only.</strong></p>
                    </div>
                  </div>
                  <Toggle checked={s.allow_write} onChange={(v) => patch({ allow_write: v })} />
                </div>
              </div>

              <div className="mt-5 pt-4 border-t border-slate-100">
                <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">What the AI can access</div>
                <div className="grid grid-cols-1 gap-3">
                  <div>
                    <div className="flex items-center gap-1.5 text-xs font-semibold text-emerald-700 mb-1.5"><Check size={13} /> Included</div>
                    <div className="flex flex-wrap gap-1.5">
                      {['Clients', 'Roster & shifts', 'Progress notes', 'Incidents', 'Staff directory'].map(t => (
                        <span key={t} className="text-xs bg-emerald-50 text-emerald-700 border border-emerald-100 px-2 py-0.5 rounded-md">{t}</span>
                      ))}
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center gap-1.5 text-xs font-semibold text-rose-600 mb-1.5"><Lock size={13} /> Never exposed</div>
                    <div className="flex flex-wrap gap-1.5">
                      {['Timesheets', 'Invoices', 'Pay & rates', 'Bank / ABA', 'Settings'].map(t => (
                        <span key={t} className="text-xs bg-rose-50 text-rose-600 border border-rose-100 px-2 py-0.5 rounded-md">{t}</span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </Card>

            <Card title="Connected assistants" icon={Plug}>
              {s.connections?.length ? (
                <div className="space-y-2">
                  {s.connections.map((c, i) => (
                    <div key={i} className="flex items-center justify-between rounded-lg border border-slate-100 px-3 py-2.5">
                      <div>
                        <div className="font-medium text-slate-700 text-sm">{c.client_name}</div>
                        <div className="text-xs text-slate-400">{c.active_tokens} active session{String(c.active_tokens) === '1' ? '' : 's'}</div>
                      </div>
                      <button onClick={() => revoke(c.client_name)} className="text-rose-500 hover:bg-rose-50 p-1.5 rounded-lg" title="Revoke access">
                        <Trash2 size={15} />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6">
                  <Plug size={22} className="text-slate-300 mx-auto mb-2" />
                  <p className="text-sm text-slate-400">No assistants connected yet.</p>
                </div>
              )}
            </Card>
          </div>
        </div>
      )}

      <p className="text-xs text-slate-400 flex items-center gap-1.5">
        <Info size={13} /> Data access always respects each user's existing Rostery permissions and organisation.
        {saving && <span className="text-slate-500 ml-1">· Saving…</span>}
      </p>
    </div>
  );
}
